import { GlobalAppStateContext, GlobalAppStateType, useGlobalAppState } from "@/utils/app-state";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { usePrevious } from "@/utils/hooks/use-previous-value";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useRouter } from "next/router";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";

const defaultGlobalAppState: GlobalAppStateType = {
  sidebarOpen: true,
  sidebarOpenPreference: true,
  adminMode: true,
  demoMode: false,
  siteVersion: undefined,
  warningBannersCollapsed: false,
};

const ROUTE_SEGMENT_PATTERN = "[^/]+";
const environmentRoute = (suffix: string) =>
  new RegExp(`^/projects/${ROUTE_SEGMENT_PATTERN}/environments/${ROUTE_SEGMENT_PATTERN}/${suffix}`);
const SQL_EXPLORER_ROUTE = environmentRoute("data-explorer/sql");
const SOURCE_CODE_ROUTE = environmentRoute("source-code");
// Dense full-page app experiences enter with Nav V2 collapsed so the working canvas gets priority.
const SETTINGS_ROUTE = environmentRoute("settings");
const ROUTES_WITH_COLLAPSED_BAR = [
  SETTINGS_ROUTE,
  SQL_EXPLORER_ROUTE,
  SOURCE_CODE_ROUTE,
  // A notebook itself, not the notebooks list: the trailing segment is the notebook id.
  environmentRoute(`notebooks/${ROUTE_SEGMENT_PATTERN}`),
];
// Both navigations render these full-page views with the sidebar collapsed on a first load, so the
// sidebar state starts closed there too. The classic nav looks the same either way; the category
// nav, which follows this state there, would otherwise visibly close as the page loads.
const ROUTES_STARTING_COLLAPSED = [SQL_EXPLORER_ROUTE, SOURCE_CODE_ROUTE];

export const shouldSidebarCollapseOnEnter = (path: string | null): boolean => {
  return path != null && ROUTES_WITH_COLLAPSED_BAR.some((regex) => regex.test(path));
};

export const GlobalAppStateWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const [globalState, setGlobalState] = useLocalStorageSyncedState<GlobalAppStateType>(
    LocalStorageKey.GLOBAL_APP_STATE,
    defaultGlobalAppState,
  );
  const [sidebarState, setSidebarState] = React.useState<
    Pick<GlobalAppStateType, "sidebarOpen" | "sidebarOpenPreference" | "sidebarClosedForSettings">
  >(() => ({
    sidebarOpen: !ROUTES_STARTING_COLLAPSED.some((regex) => regex.test(router.asPath)),
    sidebarOpenPreference: true,
  }));
  const normalizedGlobalState = useMemo(
    () => ({
      ...globalState,
      ...sidebarState,
    }),
    [globalState, sidebarState],
  );

  const updateState = useCallback(
    (newState: Partial<GlobalAppStateType>) => {
      const { sidebarOpen, sidebarOpenPreference, sidebarClosedForSettings, ...persistentState } =
        newState;

      if (
        sidebarOpen != null ||
        sidebarOpenPreference != null ||
        sidebarClosedForSettings != null
      ) {
        setSidebarState((state) => ({
          sidebarOpen: sidebarOpen ?? state.sidebarOpen,
          sidebarOpenPreference: sidebarOpenPreference ?? state.sidebarOpenPreference,
          sidebarClosedForSettings: sidebarClosedForSettings ?? state.sidebarClosedForSettings,
        }));
      }

      if (Object.keys(persistentState).length > 0) {
        setGlobalState((state) => ({ ...state, ...persistentState }));
      }
    },
    [setGlobalState],
  );

  const stateValue = useMemo(
    () => ({
      state: normalizedGlobalState,
      updateState: updateState,
    }),
    [normalizedGlobalState, updateState],
  );

  return (
    <GlobalAppStateContext.Provider value={stateValue}>{children}</GlobalAppStateContext.Provider>
  );
};

export const GlobalAppStateListener = () => {
  const { state, updateState } = useGlobalAppState();
  const collapseAnimationFrameRef = useRef<number | null>(null);
  const sidebarOpenPreferenceRef = useRef(state.sidebarOpenPreference ?? state.sidebarOpen);

  const router = useRouter();
  const path = router.asPath;
  const previousPath = usePrevious(path);

  useEffect(() => {
    sidebarOpenPreferenceRef.current = state.sidebarOpenPreference ?? state.sidebarOpen;
  }, [state.sidebarOpen, state.sidebarOpenPreference]);

  useEffect(() => {
    const clearPendingCollapse = () => {
      const animationFrameId = collapseAnimationFrameRef.current;
      if (animationFrameId == null) return;

      window.cancelAnimationFrame(animationFrameId);
      collapseAnimationFrameRef.current = null;
    };

    clearPendingCollapse();

    const pathCollapsesSidebar = shouldSidebarCollapseOnEnter(path);
    const prevPathCollapsesSidebar = shouldSidebarCollapseOnEnter(previousPath);
    // The category nav keeps its sidebar on Settings, so a page reached from there must not read
    // the closed `sidebarOpen` in the render before this restores it; it reads the preference.
    const sidebarClosedForSettings = path != null && SETTINGS_ROUTE.test(path);
    if (pathCollapsesSidebar && !prevPathCollapsesSidebar) {
      collapseAnimationFrameRef.current = window.requestAnimationFrame(() => {
        updateState({ sidebarOpen: false, sidebarClosedForSettings });
        collapseAnimationFrameRef.current = null;
      });
    } else if (pathCollapsesSidebar) {
      updateState({ sidebarClosedForSettings });
    } else if (prevPathCollapsesSidebar) {
      updateState({
        sidebarOpen: sidebarOpenPreferenceRef.current,
        sidebarClosedForSettings: false,
      });
    }

    return clearPendingCollapse;
  }, [path, previousPath, updateState]);

  return null;
};
